import clsx from 'clsx';
import {
  createContext,
  type ElementType,
  forwardRef,
  type ReactElement,
  useContext,
  useMemo,
} from 'react';

import { containerCSS } from './Container.sty.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { PolymorphicComponentProps } from '../../core/types/polymorph.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { containerColorsCSS } from '../../styles/container.sty.js';
import {
  getLayoutSizeStyles,
  type LayoutSizeProps,
} from '../../styles/getLayoutSizeStyles.js';
import {
  getSpacingSprinkles,
  type SpacingProps,
} from '../../styles/getSpacingSprinkles.js';

const COMPONENT_NAME = 'Container';

/** @internal */
export type ContainerColoring = {
  /**
   * The visual style of the container.
   * @defaultValue 'default'
   */
  variant: 'minimal' | 'default' | 'emphasized' | 'accent';
  /**
   * The color of the container. This should be chosen based on the context
   * the container is used in.
   * @defaultValue 'neutral'
   */
  color: 'primary' | 'neutral' | 'success' | 'warning' | 'critical';

  default: boolean;
};

const ContainerColoringContext = createContext<ContainerColoring>({
  variant: 'default',
  color: 'neutral',
  default: true,
});

/**
 * Context used for passing down color and variant information for accent inheritance
 * @internal
 */
export const useContainerColoring = () => {
  return useContext(ContainerColoringContext);
};

/**
 * Accepted properties for Container
 * @public
 */
export interface ContainerOwnProps
  extends WithChildren,
    DataTestId,
    StylingProps,
    SpacingProps,
    LayoutSizeProps,
    MaskingProps,
    BehaviorTrackingProps {
  /**
   * The visual style of the container.
   * @defaultValue 'default'
   */
  variant?: 'minimal' | 'default' | 'emphasized' | 'accent';
  /**
   * The color of the container. This should be chosen based on the context
   * the container is used in.
   * @defaultValue 'neutral'
   */
  color?: 'primary' | 'neutral' | 'success' | 'warning' | 'critical';
}

/**
 * Merge own props with others inherited from the underlying element type
 * @public
 */
export type ContainerProps<E extends ElementType> = PolymorphicComponentProps<
  E,
  ContainerOwnProps
>;

/**
 * Containers can be used to group content that is related. Additionally, they emphasize and highlight your grouped content.
 * @public
 */
export const Container: <E extends ElementType = 'div'>(
  props: ContainerProps<E>,
) => ReactElement | null = /* @__PURE__ */ forwardRef(
  <E extends ElementType>(
    {
      as,
      children,
      variant = 'default',
      color = 'neutral',
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      'data-dt-component': dataDtComponent = COMPONENT_NAME,
      ...remainingProps
    }: ContainerProps<E>,
    ref: typeof remainingProps.ref,
  ) => {
    const [spacingClasses, sprinkleRestProps] = getSpacingSprinkles(
      remainingProps,
      { padding: 16 },
    );

    const [sizeStyles, sizeRestProps] = getLayoutSizeStyles(sprinkleRestProps);

    const [behavioralTrackingProps, finalRestProps] =
      useBehavioralTrackingProps(sizeRestProps);

    // Make the component polymorphic
    const Component = as || 'div';

    const context = useMemo(
      () => ({ color, variant, default: false }),
      [color, variant],
    );

    return (
      <ContainerColoringContext.Provider value={context}>
        <Component
          ref={ref}
          className={clsx(
            containerColorsCSS({ color, variant }),
            containerCSS,
            spacingClasses,
            consumerClassName,
          )}
          style={{
            ...sizeStyles,
            ...consumerStyle,
          }}
          data-testid={dataTestId}
          data-dtrum-mask={dataDtrumMask}
          data-dtrum-allow={dataDtrumAllow}
          {...finalRestProps}
          {...behavioralTrackingProps}
          data-dt-component={dataDtComponent}
        >
          {children}
        </Component>
      </ContainerColoringContext.Provider>
    );
  },
);

(Container as typeof Container & { displayName: string }).displayName =
  'Container';
